/**
 * Chart theme + shared drawing constants.
 *
 * Kept next to the Tailwind palette so the canvas and the DOM never drift
 * apart: an "up" candle and an "up" badge must be the same green.
 */

import { ColorType, CrosshairMode, LineStyle } from 'lightweight-charts'
import type { DeepPartial, ChartOptions } from 'lightweight-charts'

export const COLORS = {
  background: '#131722',
  panel: '#1e222d',
  grid: '#1f2430',
  border: '#2a2e39',
  text: '#d1d4dc',
  textDim: '#787b86',
  up: '#26a69a',
  down: '#ef5350',
  accent: '#2962ff',
  warn: '#ff9800',
  entry: '#2962ff',
  stop: '#ef5350',
  target: '#26a69a',
  support: 'rgba(38, 166, 154, 0.12)',
  supportBorder: 'rgba(38, 166, 154, 0.45)',
  resistance: 'rgba(239, 83, 80, 0.12)',
  resistanceBorder: 'rgba(239, 83, 80, 0.45)',
  fib: 'rgba(255, 152, 0, 0.55)',
} as const

export const chartOptions: DeepPartial<ChartOptions> = {
  layout: {
    background: { type: ColorType.Solid, color: COLORS.background },
    textColor: COLORS.text,
    fontSize: 11,
    fontFamily: '-apple-system, BlinkMacSystemFont, Trebuchet MS, Roboto, sans-serif',
    attributionLogo: false,
  },
  grid: {
    vertLines: { color: COLORS.grid },
    horzLines: { color: COLORS.grid },
  },
  crosshair: {
    mode: CrosshairMode.Normal,
    vertLine: { color: COLORS.textDim, width: 1, style: LineStyle.Dashed, labelBackgroundColor: COLORS.panel },
    horzLine: { color: COLORS.textDim, width: 1, style: LineStyle.Dashed, labelBackgroundColor: COLORS.panel },
  },
  rightPriceScale: { borderColor: COLORS.border, scaleMargins: { top: 0.08, bottom: 0.08 } },
  timeScale: {
    borderColor: COLORS.border,
    timeVisible: true,
    secondsVisible: false,
    rightOffset: 6,
  },
  handleScroll: { mouseWheel: true, pressedMouseMove: true },
  handleScale: { mouseWheel: true, pinch: true },
}

export const candleOptions = {
  upColor: COLORS.up,
  downColor: COLORS.down,
  borderUpColor: COLORS.up,
  borderDownColor: COLORS.down,
  wickUpColor: COLORS.up,
  wickDownColor: COLORS.down,
}

/** Which overlays exist, in the order the toggle bar shows them. */
export const OVERLAY_GROUPS = [
  { key: 'ema', label: 'EMA', series: ['ema_fast', 'ema_mid', 'ema_slow'] },
  { key: 'bb', label: 'Bollinger', series: ['bb_upper', 'bb_mid', 'bb_lower'] },
] as const

export const TIMEFRAMES = ['15m', '1h', '4h', '1d', '1w'] as const
export type TimeframeKey = (typeof TIMEFRAMES)[number]

export const TIMEFRAME_SECONDS: Record<string, number> = {
  '1m': 60, '5m': 300, '15m': 900, '1h': 3600, '4h': 14400, '1d': 86400, '1w': 604800,
}

/**
 * Start of the bucket containing `epochSeconds`, mirroring Timeframe.floor.
 *
 * Needed because the two feeds publish at different granularities: Binance
 * streams a kline per subscribed timeframe, while the Vietnamese adapter
 * aggregates ticks into 1-minute bars and publishes only those. A 15m VN
 * chart therefore never receives a 15m tick and would sit frozen unless the
 * 1m ticks are folded into the bucket on screen.
 */
export function bucketStart(epochSeconds: number, timeframe: string): number {
  const size = TIMEFRAME_SECONDS[timeframe]
  if (!size) return epochSeconds
  if (timeframe === '1w') {
    // ISO weeks open Monday 00:00 UTC; the epoch itself was a Thursday, so
    // dividing by a week would land the boundary three days out.
    const moment = new Date(epochSeconds * 1000)
    const sinceMonday = (moment.getUTCDay() + 6) % 7
    return Date.UTC(
      moment.getUTCFullYear(), moment.getUTCMonth(), moment.getUTCDate() - sinceMonday,
    ) / 1000
  }
  return Math.floor(epochSeconds / size) * size
}

/** Trigger timeframe -> horizon, mirroring MTF_GROUPS in the backend. */
export const TIMEFRAME_HORIZON: Record<string, string> = {
  '15m': 'scalp',
  '1h': 'intraday',
  '4h': 'swing',
  '1d': 'position',
}
